import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  IoArrowForward,
  IoCheckmark,
  IoSparkles,
  IoChevronDown,
} from 'react-icons/io5';
import Button from '../components/Button.jsx';
import { getVisibleInvitationCategories } from '../lib/invitationCatalog.js';
import {
  STANDARD_PACKAGE_NAME,
  STANDARD_PACKAGE_PRICE,
  STANDARD_PACKAGE_TURNAROUND,
  standardPackageFeatures,
  bookingAddOnOptions,
  bookingNotes,
  featureCategories,
  trustSignals,
  priceBreakdown,
  processTimeline,
  formatPeso,
} from '../lib/pricingData.js';
import '../styles/Pricing.css';

const Pricing = () => {
  const categoryLinks = getVisibleInvitationCategories();
  const [expandedFaq, setExpandedFaq] = useState(0);
  const [showComparison, setShowComparison] = useState(false);
  const [showAddOns, setShowAddOns] = useState(false);
  const [expandedFeatureCategory, setExpandedFeatureCategory] = useState(null);
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  const handleCategoryClick = (categoryId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('Clicking category:', categoryId, 'Currently expanded:', expandedFeatureCategory);
    setExpandedFeatureCategory(
      expandedFeatureCategory === categoryId ? null : categoryId
    );
  };

  const handleToggleAllFeatures = () => {
    setShowAllFeatures((value) => !value);
  };

  const handleToggleAddOns = () => {
    setShowAddOns((value) => !value);
  };

  const faqItems = [
    {
      q: 'How much is the standard package?',
      a: `${STANDARD_PACKAGE_NAME} is ${formatPeso(STANDARD_PACKAGE_PRICE)} and already includes the core invitation website features.`,
    },
    {
      q: 'How long is the normal turnaround?',
      a: `Standard delivery is ${STANDARD_PACKAGE_TURNAROUND} once complete and final event details are submitted.`,
    },
    {
      q: 'Can I request rush delivery?',
      a: 'Yes. RSVP rush order and full e-invitation rush order are both available as add-ons for same-day handling.',
    },
    {
      q: 'How many photos are included?',
      a: 'The standard package includes up to 10 photos. Add the additional pictures option if you need a larger gallery.',
    },
    {
      q: 'What happens after booking?',
      a: 'Submit the form, send the complete event details, and we will confirm the package, add-ons, and production schedule with you.',
    },
  ];

  return (
    <div className="pricing-page">
      <section className="pp-hero">
        <h1>Transparent Pricing</h1>
        <p>One package. Flexible add-ons. Fast turnaround.</p>
      </section>

      <section className="pp-trust-signals">
        <div className="pp-trust-container">
          {trustSignals.map((signal) => (
            <div key={signal.label} className="pp-trust-item">
              <span className="pp-trust-icon">{signal.icon}</span>
              <strong>{signal.label}</strong>
              <p>{signal.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pp-feature-breakdown">
        <h2>What's Included</h2>
        <div className="pp-breakdown-chart">
          {featureCategories.map((category) => (
            <div key={category.id} className="pp-breakdown-item">
              <span className="pp-breakdown-icon">{category.icon}</span>
              <strong className="pp-breakdown-name">{category.name}</strong>
              <span className="pp-breakdown-count">{category.count}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="pp-cards">
        <div className="pp-card pp-card--featured">
          <div className="pp-badge">
            <IoSparkles className="pp-badge-icon" />
            <span>Standard Package</span>
          </div>

          <div className="pp-card-header">
            <span className="pp-type">{STANDARD_PACKAGE_NAME}</span>
            <div className="pp-amount">
              <span className="pp-price">{formatPeso(STANDARD_PACKAGE_PRICE)}</span>
            </div>
            <span className="pp-note">
              Up to 10 photos - {STANDARD_PACKAGE_TURNAROUND} turnaround
            </span>
          </div>

          <div className="pp-features">
            <ul>
              {standardPackageFeatures.slice(0, 5).map((feature) => (
                <li key={feature}>
                  <IoCheckmark className="pp-check pp-check--gold" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <button
            type="button"
            className="pp-toggle-all-features"
            onClick={handleToggleAllFeatures}
            aria-expanded={showAllFeatures}
          >
            <span>{showAllFeatures ? 'Hide' : 'View'} all 30 features</span>
            <IoChevronDown className={`pp-toggle-icon ${showAllFeatures ? 'open' : ''}`} />
          </button>

          {showAllFeatures && (
            <div className="pp-features pp-features-expanded">
              <ul>
                {standardPackageFeatures.slice(5).map((feature) => (
                  <li key={feature}>
                    <IoCheckmark className="pp-check pp-check--gold" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Link to="/book" className="pp-cta-link">
            <Button variant="primary" size="large" className="pp-btn">
              Book Now
              <IoArrowForward className="pp-btn-icon" />
            </Button>
          </Link>
        </div>

        <div className="pp-card pp-card--addons">
          <div className="pp-card-header">
            <span className="pp-type">Optional Add-ons</span>
            <div className="pp-amount">
              <span className="pp-price">{formatPeso(200)}</span>
              <span className="pp-sep">-</span>
              <span className="pp-price">{formatPeso(1000)}</span>
            </div>
          </div>

          <button
            type="button"
            className="pp-toggle-addons"
            onClick={handleToggleAddOns}
            aria-expanded={showAddOns}
          >
            <span>{showAddOns ? 'Hide' : 'Show'} {bookingAddOnOptions.length} add-ons</span>
            <IoChevronDown className={`pp-toggle-icon ${showAddOns ? 'open' : ''}`} />
          </button>

          {showAddOns && (
            <div className="pp-features pp-addons-list">
              <ul>
                {bookingAddOnOptions.map((addOn) => (
                  <li key={addOn.id}>
                    <div className="pp-addon-header">
                      <strong>{addOn.label}</strong>
                      <p className="pp-addon-description">{addOn.description}</p>
                    </div>
                    <span className="pp-addon-price">{formatPeso(addOn.price)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Link to="/book" className="pp-cta-link">
            <Button variant="outline-primary" size="large" className="pp-btn">
              Customize Booking
            </Button>
          </Link>
        </div>
      </section>

      <section className="pp-feature-categories">
        <h2>Explore All Features</h2>
        <div className="pp-categories-grid">
          {featureCategories.map((category) => {
            const isExpanded = expandedFeatureCategory === category.id;
            return (
              <div key={category.id} className="pp-category-card">
                <button
                  type="button"
                  className="pp-category-header"
                  onClick={(e) => handleCategoryClick(category.id, e)}
                  aria-expanded={isExpanded}
                >
                  <span className="pp-category-icon">{category.icon}</span>
                  <div className="pp-category-title">
                    <strong>{category.name}</strong>
                    <span className="pp-category-count">({category.count})</span>
                  </div>
                  <IoChevronDown
                    className={`pp-category-chevron ${isExpanded ? 'open' : ''}`}
                  />
                </button>
                {isExpanded && (
                  <div className="pp-category-features">
                    <ul>
                      {category.features.map((feature) => (
                        <li key={feature}>
                          <IoCheckmark className="pp-check pp-check--gold" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="pp-price-breakdown">
        <h2>Price Breakdown</h2>
        <p className="pp-breakdown-subtitle">What Your {formatPeso(STANDARD_PACKAGE_PRICE)} Investment Covers</p>
        <div className="pp-breakdown-container">
          <div className="pp-breakdown-items">
            {priceBreakdown.map((item) => (
              <div key={item.label} className="pp-breakdown-item-detail">
                <div className="pp-breakdown-label">
                  <strong>{item.label}</strong>
                  <span className="pp-breakdown-amount">{formatPeso(item.amount)}</span>
                </div>
                <div className="pp-breakdown-bar">
                  <div
                    className="pp-breakdown-fill"
                    style={{ width: `${item.percentage}%` }}
                  ></div>
                </div>
                <span className="pp-breakdown-percent">{item.percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pp-process-timeline">
        <h2>Your Journey</h2>
        <p className="pp-timeline-subtitle">From idea to reality in 4 steps</p>
        <div className="pp-timeline">
          {processTimeline.map((step, index) => (
            <div key={step.step} className="pp-timeline-item">
              <div className="pp-timeline-marker">
                <span className="pp-timeline-number">{step.step}</span>
                {index < processTimeline.length - 1 && <div className="pp-timeline-line"></div>}
              </div>
              <div className="pp-timeline-content">
                <strong>{step.label}</strong>
                <p>{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="pp-terms">
        <div className="pp-terms-inner">
          <span className="pp-terms-icon">i</span>
          <div>
            <strong>Booking Info:</strong> {bookingNotes[0]} {bookingNotes[1]}
          </div>
        </div>
      </section>

      <section className="pp-faq-section">
        <div className="pp-faq-header">
          <h2>Questions?</h2>
          <button
            type="button"
            className="pp-toggle-comparison"
            onClick={() => setShowComparison((value) => !value)}
            aria-expanded={showComparison}
          >
            {showComparison ? 'Hide' : 'Compare'} invitation categories
          </button>
        </div>

        {showComparison && (
          <div className="pp-link-grid pp-link-grid--compact">
            {categoryLinks.map((category) => (
              <Link key={category.value} to={category.path} className="pp-link-card">
                <span>{category.label}</span>
                <strong>{category.headline}</strong>
              </Link>
            ))}
          </div>
        )}

        <div className="pp-faq-accordion">
          {faqItems.map((item, index) => (
            <div key={item.q} className="pp-faq-item">
              <button
                type="button"
                className="pp-faq-question"
                onClick={() => setExpandedFaq(expandedFaq === index ? -1 : index)}
                aria-expanded={expandedFaq === index}
              >
                <span>{item.q}</span>
                <IoChevronDown className={`pp-faq-icon ${expandedFaq === index ? 'open' : ''}`} />
              </button>
              {expandedFaq === index && (
                <div className="pp-faq-answer">
                  <p>{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="pp-support-links">
          <Link to="/invitations">Browse all designs</Link>
          <Link to="/digital-invitation-faq">Read the full FAQ</Link>
        </div>
      </section>

      <section className="pp-cta">
        <h2>Ready to create your invitation?</h2>
        <Link to="/book">
          <Button variant="primary" size="large">
            Get Started
            <IoArrowForward className="pp-btn-icon" />
          </Button>
        </Link>
      </section>
    </div>
  );
};

export default Pricing;
